import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

export function getPermissionActionLabel(action) {
  if (action?.module_action_name) {
    return action.module_action_name;
  }
  const code = action?.module_action_code;
  if (code) {
    return String(code)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return "Permission";
}

export function getPermissionActionId(action) {
  return Number(action?.application_module_action_id);
}

export default function PermissionSelector({
  groups = [],
  allowedIds = new Set(),
  onToggle,
  onToggleGroup,
  disabled = false,
  showSelectAll = true,
  getButtonSx,
}) {
  const groupOptions = useMemo(
    () =>
      groups.filter(
        (group) => Array.isArray(group.actions) && group.actions.length > 0
      ),
    [groups]
  );

  const [selectedGroupKey, setSelectedGroupKey] = useState("");

  useEffect(() => {
    if (!groupOptions.length) {
      setSelectedGroupKey("");
      return;
    }
    const exists = groupOptions.some(
      (group) => group.label === selectedGroupKey
    );
    if (!selectedGroupKey || !exists) {
      setSelectedGroupKey(groupOptions[0].label);
    }
  }, [groupOptions, selectedGroupKey]);

  const selectedGroup = groupOptions.find(
    (group) => group.label === selectedGroupKey
  );

  const getGroupSelectedCount = (actions) =>
    (actions || []).filter((action) =>
      allowedIds.has(getPermissionActionId(action))
    ).length;

  const isGroupFullySelected = (actions) =>
    (actions || []).length > 0 &&
    (actions || []).every((action) =>
      allowedIds.has(getPermissionActionId(action))
    );

  if (!groupOptions.length) {
    return (
      <Typography color="text.secondary">
        No module actions found. Run WalletAdminRbacSeeder to seed actions.
      </Typography>
    );
  }

  const actions = selectedGroup?.actions || [];
  const selectedCount = getGroupSelectedCount(actions);
  const allSelected = isGroupFullySelected(actions);

  return (
    <Stack spacing={2}>
      <FormControl fullWidth size="small">
        <InputLabel id="permission-group-label">Permission group</InputLabel>
        <Select
          labelId="permission-group-label"
          label="Permission group"
          value={selectedGroupKey}
          onChange={(e) => setSelectedGroupKey(e.target.value)}
        >
          {groupOptions.map((group) => (
            <MenuItem key={group.label} value={group.label}>
              {group.label} ({getGroupSelectedCount(group.actions)}/
              {group.actions.length})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedGroup && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {selectedCount} of {actions.length} selected in{" "}
              {selectedGroup.label}
            </Typography>
            {showSelectAll && !disabled && onToggleGroup && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onToggleGroup(actions, false)}
                  disabled={selectedCount === 0}
                  sx={{ textTransform: "none" }}
                >
                  Clear group
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onToggleGroup(actions, !allSelected)}
                  sx={{ textTransform: "none" }}
                >
                  {allSelected ? "Deselect all" : "Select all"}
                </Button>
              </Stack>
            )}
          </Stack>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {actions.map((action) => {
              const actionId = getPermissionActionId(action);
              const selected = allowedIds.has(actionId);
              const label = getPermissionActionLabel(action);

              return (
                <Button
                  key={actionId || label}
                  size="small"
                  variant={selected ? "contained" : "outlined"}
                  color={selected ? "primary" : "inherit"}
                  disabled={disabled || !actionId}
                  onClick={() => onToggle(actionId)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: selected ? 600 : 400,
                    ...(getButtonSx ? getButtonSx(actionId, selected) : {}),
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
